import { ArrowLeft, Calendar, Clock, Box, BookOpen, Twitter, Share2 } from 'lucide-react';
import { useParams, Navigate } from 'react-router-dom';
import { getBlogPostBySlug } from '../data/blogPosts';
import { format } from 'date-fns';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Simple markdown parser for blog content
function parseMarkdown(content: string): JSX.Element[] {
  const lines = content.trim().split('\n');
  const elements: JSX.Element[] = [];
  let currentList: string[] = [];
  let currentTable: string[][] = [];
  let inCodeBlock = false;
  let codeBlockContent = '';
  let codeBlockLang = '';
  let key = 0;

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={key++} className="list-disc list-inside space-y-2 text-gray-700 mb-6">
          {currentList.map((item, i) => (
            <li key={i} className="leading-relaxed">{parseInline(item)}</li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };

  const flushTable = () => {
    if (currentTable.length > 0) {
      const headers = currentTable[0];
      const rows = currentTable.slice(2); // Skip header separator
      elements.push(
        <div key={key++} className="overflow-x-auto mb-6">
          <table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                {headers.map((header, i) => (
                  <th key={i} className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                    {header.trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200">
                      {parseInline(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      currentTable = [];
    }
  };

  const parseInline = (text: string): React.ReactNode => {
    // Bold
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Italic
    text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // Inline code
    text = text.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-gray-100 text-[#FF6B6B] rounded text-sm font-mono">$1</code>');
    // Links
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#4ECDC4] hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');

    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <div key={key++} className="mb-6 rounded-xl overflow-hidden">
            <SyntaxHighlighter
              language={codeBlockLang || 'text'}
              style={vscDarkPlus}
              customStyle={{
                margin: 0,
                padding: '1.5rem',
                borderRadius: '0.75rem',
                fontSize: '0.875rem',
              }}
            >
              {codeBlockContent.trim()}
            </SyntaxHighlighter>
          </div>
        );
        codeBlockContent = '';
        codeBlockLang = '';
        inCodeBlock = false;
      } else {
        flushList();
        flushTable();
        inCodeBlock = true;
        codeBlockLang = line.slice(3).trim();
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockContent += line + '\n';
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      flushList();
      flushTable();
      continue;
    }

    // Table row
    if (line.includes('|') && line.trim().startsWith('|')) {
      flushList();
      const cells = line.split('|').filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1);
      currentTable.push(cells);
      continue;
    }

    // Headers
    if (line.startsWith('## ')) {
      flushList();
      flushTable();
      elements.push(
        <h2 key={key++} className="text-2xl font-bold text-gray-900 mt-10 mb-4">
          {line.slice(3)}
        </h2>
      );
      continue;
    }

    if (line.startsWith('### ')) {
      flushList();
      flushTable();
      elements.push(
        <h3 key={key++} className="text-xl font-bold text-gray-900 mt-8 mb-3">
          {line.slice(4)}
        </h3>
      );
      continue;
    }

    if (line.startsWith('#### ')) {
      flushList();
      flushTable();
      elements.push(
        <h4 key={key++} className="text-lg font-bold text-gray-900 mt-6 mb-2">
          {line.slice(5)}
        </h4>
      );
      continue;
    }

    // Horizontal rule
    if (line.trim() === '---') {
      flushList();
      flushTable();
      elements.push(
        <hr key={key++} className="my-8 border-gray-200" />
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(line)) {
      flushTable();
      const match = line.match(/^\d+\.\s(.*)$/);
      if (match) {
        currentList.push(match[1]);
      }
      continue;
    }

    // Bullet list
    if (line.startsWith('- ')) {
      flushTable();
      currentList.push(line.slice(2));
      continue;
    }

    // Regular paragraph
    flushList();
    flushTable();
    elements.push(
      <p key={key++} className="text-gray-700 leading-relaxed mb-4">
        {parseInline(line)}
      </p>
    );
  }

  flushList();
  flushTable();

  return elements;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formattedDate = format(new Date(post.publishedAt), 'MMMM d, yyyy');

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.description,
          url,
        });
      } catch {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-200 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6B6B] rounded-lg flex items-center justify-center rotate-3">
              <Box className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900">JsonToonCraft</span>
          </a>
          <div className="flex items-center gap-3">
            <a
              href="/blog"
              className="hidden sm:flex items-center gap-2 px-4 py-2 text-[#1A535C] hover:bg-gray-100 rounded-lg text-sm font-medium transition-all"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </a>
            <a
              href="/playground"
              className="px-5 py-2 bg-[#FF6B6B] text-white rounded-lg text-sm font-medium hover:bg-[#ff5252] transition-all hover:scale-105"
            >
              Playground
            </a>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <a
            href="/blog"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Blog</span>
          </a>

          {/* Article header */}
          <header className="mb-10">
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-[#4ECDC4]/10 text-[#1A535C] rounded-full text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            <p className="text-xl text-gray-600 mb-6">
              {post.description}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 pb-8 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] rounded-full flex items-center justify-center text-white font-bold">
                  {post.author.name.charAt(0)}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{post.author.name}</div>
                  {post.author.twitter && (
                    <a
                      href={`https://twitter.com/${post.author.twitter}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#4ECDC4] hover:underline"
                    >
                      @{post.author.twitter}
                    </a>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formattedDate}</span>
              </div>

              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{post.readingTime} min read</span>
              </div>

              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={handleShare}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Share"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                {post.author.twitter && (
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}&via=${post.author.twitter}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Share on Twitter"
                  >
                    <Twitter className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </header>

          {/* Article content */}
          <div className="prose prose-lg max-w-none">
            {parseMarkdown(post.content)}
          </div>

          {/* Article footer */}
          <footer className="mt-12 pt-8 border-t border-gray-200">
            <div className="bg-gradient-to-br from-[#1A535C] to-[#14424a] rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-3">
                Ready to try JsonToonCraft?
              </h3>
              <p className="text-white/80 mb-6">
                Convert JSON to TOON and save 30-60% on LLM token costs.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="/playground"
                  className="px-6 py-3 bg-[#FF6B6B] text-white rounded-xl font-medium hover:bg-[#ff5252] transition-all hover:scale-105"
                >
                  Try Playground
                </a>
                <a
                  href="https://marketplace.visualstudio.com/items?itemName=krishcdbry.jsontooncraft"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-all"
                >
                  VS Code Extension
                </a>
              </div>
            </div>
          </footer>
        </div>
      </article>

      {/* Site Footer */}
      <footer className="py-12 px-6 bg-white border-t border-gray-200">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6B6B] rounded-lg flex items-center justify-center rotate-3">
              <Box className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900">JsonToonCraft</span>
          </div>
          <p className="text-sm text-gray-600">
            Built with love by{' '}
            <a
              href="https://krishcdbry.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FF6B6B] font-medium hover:underline"
            >
              Krish
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
